import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { QuoteServiceLineChangeReview as Review } from './quoteReviewContract';
import { QuoteConfirmationReview } from './QuoteConfirmationReview';

/** All prices and derived effects come from the server. The complete result is visible before consent. */
export function QuoteServiceLineChangeReview({ review }: { review: Review }) {
  const { t, i18n } = useTranslation('assistant');
  const locale = i18n.resolvedLanguage ?? i18n.language ?? 'en';
  const number = (n: number) =>
    new Intl.NumberFormat(locale, { maximumFractionDigits: 8 }).format(n);
  const money = (n: number) =>
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: review.quote.pricing.currency,
      maximumFractionDigits: 6,
    }).format(n / 100);
  const value = (field: string, v: unknown): string =>
    v === null || v === undefined
      ? t('quoteReview.none')
      : typeof v === 'number'
        ? field.endsWith('Cents')
          ? `${money(v)} ${t('quoteReview.netLabel')}`
          : number(v)
        : String(v);
  const fields: Record<string, string> = {
    description: 'description',
    name: 'description',
    estimatedHours: 'plannedHours',
    discountPercent: 'discount',
    quantity: 'quantity',
    unitPriceCents: 'unitNet',
    amountCents: 'unitNet',
    vatPercent: 'vat',
    notes: 'notes',
    unit: 'unit',
    fixedPriceOverrideCents: 'fixedPrice',
  };
  return (
    <Box data-testid="quote-service-line-change-review">
      <Typography variant="subtitle2">{t('quoteServiceLine.changes')}</Typography>
      {review.changes.map((change) => (
        <Box
          key={`${change.kind}-${change.lineId}`}
          sx={{
            my: 1,
            pl: 1,
            borderLeft: '3px solid',
            borderColor: 'warning.main',
            textDecoration: change.change === 'removed' ? 'line-through' : undefined,
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            {t(`quoteServiceLine.${change.change}`)} · {t(`quoteServiceLine.${change.kind}`)}
          </Typography>
          {Object.keys(change.after ?? change.before ?? {})
            .filter((field) => field !== 'id' && field !== 'sortOrder')
            .map((field) => {
              const before = (change.before as unknown as Record<string, unknown> | null)?.[field];
              const after = (change.after as unknown as Record<string, unknown> | null)?.[field];
              const changed =
                change.changedFields.includes(field as never) || change.change !== 'updated';
              return (
                <Typography
                  key={field}
                  variant="body2"
                  data-changed={changed}
                  sx={{
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'anywhere',
                    bgcolor: changed ? 'warning.light' : undefined,
                  }}
                >
                  <strong>
                    {t(`quoteReview.${fields[field] ?? field}`, { defaultValue: field })}:{' '}
                  </strong>
                  {value(field, before)}
                  {changed ? ` → ${value(field, after)}` : ''}
                </Typography>
              );
            })}
        </Box>
      ))}
      {review.derived.services.map((s) => (
        <Typography key={s.bookingServiceId} variant="body2">
          {t('quoteReview.plannedHours')}: {value('hours', s.plannedHoursBefore)} →{' '}
          {value('hours', s.plannedHoursAfter)} h · {t('quoteServiceLine.pricingMode')}:{' '}
          {t(`quoteServiceLine.${s.pricingModeBefore}`)} →{' '}
          {t(`quoteServiceLine.${s.pricingModeAfter}`)}
        </Typography>
      ))}
      <Typography variant="body2">
        {t('quoteServiceLine.duration')}:{' '}
        {value('hours', review.derived.estimatedDurationHoursBefore)} →{' '}
        {value('hours', review.derived.estimatedDurationHoursAfter)} h
      </Typography>
      {review.notes.map((note) => (
        <Typography key={note} variant="body2" color="warning.main">
          {t(`quoteServiceLine.notes.${note}`)}
        </Typography>
      ))}
      {(['netSubtotalCents', 'taxCents', 'grossTotalCents'] as const).map((field, index) => (
        <Typography
          key={field}
          variant="body2"
          sx={{
            bgcolor:
              review.quote.pricing[field] !== review.result.pricing[field]
                ? 'warning.light'
                : undefined,
          }}
        >
          <strong>{t(`quoteReview.${['net', 'tax', 'gross'][index]}`)}: </strong>
          {money(review.quote.pricing[field])} → {money(review.result.pricing[field])}
        </Typography>
      ))}
      <Typography variant="subtitle2" sx={{ mt: 1 }}>
        {t('quoteServiceLine.result')}
      </Typography>
      <QuoteConfirmationReview
        review={{
          action: 'serviceLine',
          quote: {
            ...review.quote,
            services: review.result.services,
            pricing: review.result.pricing,
          },
        }}
      />
      <Typography variant="body2">{t('quoteServiceLine.noOtherEffects')}</Typography>
    </Box>
  );
}
